/** Form controls styled like the designs' tab switchers: segmented choice, switch and slider. */
import { useRef, type CSSProperties, type KeyboardEvent } from 'react';
import s from './Controls.module.css';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  hint?: string;
}

/** A radio group drawn as the designs' pill tabs. Arrow keys move the choice. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  size = 'md',
  className,
}: {
  value: T;
  options: SegmentedOption<T>[];
  onChange: (v: T) => void;
  label: string;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKey = (e: KeyboardEvent, i: number) => {
    const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!dir) return;
    e.preventDefault();
    const next = (i + dir + options.length) % options.length;
    const opt = options[next];
    if (!opt) return;
    onChange(opt.value);
    refs.current[next]?.focus();
  };
  return (
    <div role="radiogroup" aria-label={label} className={[s.seg, size === 'sm' ? s.segSm : '', className ?? ''].join(' ')}>
      {options.map((o, i) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            className={[s.segItem, on ? s.segOn : ''].join(' ')}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => onKey(e, i)}
          >
            {o.label}
            {o.hint && <span className={s.segHint}>{o.hint}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function Switch({ checked, onChange, label, id }: { checked: boolean; onChange: (v: boolean) => void; label: string; id?: string }) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={[s.switch, checked ? s.switchOn : ''].join(' ')}
      onClick={() => onChange(!checked)}
    >
      <span className={s.knob} aria-hidden="true" />
    </button>
  );
}

export function Slider({
  value,
  onChange,
  label,
  disabled,
  id,
}: {
  /** 0–1. */
  value: number;
  onChange: (v: number) => void;
  label: string;
  disabled?: boolean;
  id?: string;
}) {
  const pct = Math.round(value * 100);
  return (
    <div className={[s.slider, disabled ? s.sliderOff : ''].join(' ')}>
      <input
        id={id}
        type="range"
        min={0}
        max={100}
        step={5}
        value={pct}
        disabled={disabled}
        aria-label={label}
        aria-valuetext={`${pct}%`}
        onChange={(e) => onChange(Number(e.target.value) / 100)}
        style={{ '--fill': `${pct}%` } as CSSProperties}
      />
      <span className={s.sliderValue} aria-hidden="true">
        {pct}%
      </span>
    </div>
  );
}
